'use client';

/* cspell:ignore Kaisen */

import React, { useCallback, useEffect, useState } from 'react';

// `motion` was previously imported here just to animate the sidebar's
// width on hover. Since this file renders on every page in the layout
// chunk, the entire motion library (~80KB minified) was being shipped
// to every visitor for a single CSS-replaceable transition. Use a CSS
// transition on `width` instead — same visual, zero JS cost.
import type { CSSProperties } from 'react';
import { Home, LogOut, Music2, Settings, User, Zap } from 'lucide-react';
import Image from '@/components/ui/image';
import Link from '@/components/ui/link';
import { useLocation } from 'react-router';
import { useRouter } from '@/lib/router';

import {
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu';

import { CommandSearch } from '@/components/command/search';
import { signOut, useSession } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { censorEmail } from '@/utils/censor-email';

import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { DropdownMenu, DropdownMenuItem } from '../ui/dropdown-menu';

type NavigationItem = {
  name: string;
  href?: string;
  icon: React.ComponentType<{ className?: string }>;
  onClick?: () => void;
  className?: string;
};

const discoverIconMaskStyle: CSSProperties = {
  display: 'inline-block',
  backgroundColor: 'currentColor',
  maskImage: 'url(/svg/discover.svg)',
  maskSize: 'contain',
  maskRepeat: 'no-repeat',
  maskPosition: 'center',
  WebkitMaskImage: 'url(/svg/discover.svg)',
  WebkitMaskSize: 'contain',
  WebkitMaskRepeat: 'no-repeat',
  WebkitMaskPosition: 'center',
};

function DiscoverIcon({
  className = '',
}: {
  className?: string;
}): React.ReactElement {
  return (
    <span
      className={className}
      style={discoverIconMaskStyle}
      aria-hidden="true"
    />
  );
}

DiscoverIcon.defaultProps = {
  className: undefined,
};

// Narrow user shape needed by this component to avoid unsafe non-null assertions
type SessionUser = {
  id: string;
  name?: string | null;
  email?: string | null;
  image?: string | null;
};

// Note: Mobile bottom nav items are defined in `mobile-bottom-nav.tsx`

const navigation: NavigationItem[] = [
  { name: 'Home', href: '/', icon: Home },
  { name: 'Discover', href: '/discover', icon: DiscoverIcon },
  { name: 'Music', href: '/music', icon: Music2 },
  { name: 'Now', href: '/now', icon: Zap, className: 'animate-pulse' },
  { name: 'Settings', href: '/settings', icon: Settings },
];

// Notifications are handled by the mobile top nav

function SidebarLogo({
  isExpanded,
}: {
  isExpanded: boolean;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'flex h-16 flex-shrink-0 items-center px-3',
        isExpanded ? 'justify-start' : 'justify-center'
      )}
    >
      {' '}
      <Link href="/" className="relative flex h-8 items-center">
        {' '}
        <Image
          src="/logo.svg"
          alt="1Anime"
          width={52}
          height={52}
          className={cn(
            'transition-[opacity,transform] duration-100 ease-in',
            isExpanded
              ? 'pointer-events-none absolute scale-90 opacity-0'
              : 'scale-100 opacity-100'
          )}
        />
        <Image
          src="https://1ani.me/assets/logotext.svg"
          alt="1Anime"
          width={100}
          height={32}
          className={cn(
            'transition-[opacity,transform] duration-100 ease-in',
            isExpanded
              ? 'scale-100 opacity-100 delay-100'
              : 'pointer-events-none absolute scale-90 opacity-0'
          )}
        />
      </Link>
    </div>
  );
}

function NavIcon({
  Icon,
  iconColorClass,
  className,
}: {
  Icon: NavigationItem['icon'];
  iconColorClass: string;
  className: string;
}): React.ReactElement {
  return (
    <Icon
      className={cn(
        'h-5 w-5 flex-shrink-0',
        'transition-none',
        iconColorClass,
        className
      )}
    />
  );
}

function SidebarNavItemRow({
  name,
  href = '',
  Icon,
  className,
  isExpanded,
  pathname,
}: {
  name: string;
  href: string;
  Icon: NavigationItem['icon'];
  className: string;
  isExpanded: boolean;
  pathname: string | null;
}): React.ReactElement {
  const isActive = pathname === href;
  const commonClasses = cn(
    'flex items-center gap-3 px-3 py-2.5',
    'text-sm font-medium relative group',
    'transition-colors duration-75 ease-in',
    'rounded-md'
  );

  const iconColorClass = isActive
    ? 'text-primary'
    : 'text-muted-foreground group-hover:text-foreground';

  const activeClasses = 'bg-primary/10 text-primary';
  const inactiveClasses =
    'text-muted-foreground hover:text-foreground hover:bg-muted';

  const content = (
    <>
      <NavIcon
        Icon={Icon}
        iconColorClass={iconColorClass}
        className={className}
      />
      <span
        className={cn(
          'whitespace-nowrap',
          'transition-[opacity,transform] duration-75',
          isExpanded
            ? 'translate-x-0 opacity-100'
            : 'pointer-events-none -translate-x-3 opacity-0',
          isActive ? 'font-semibold' : ''
        )}
      >
        {name}
      </span>

      {!isExpanded && (
        <span
          className={cn(
            'absolute left-full top-1/2 ml-3 -translate-y-1/2 px-2.5 py-1.5',
            'rounded-md border border-border bg-popover text-xs text-popover-foreground shadow-md',
            'pointer-events-none translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100',
            'z-50 whitespace-nowrap transition-[opacity,transform] duration-75'
          )}
        >
          {name}
        </span>
      )}
    </>
  );

  return (
    <Link
      key={href}
      href={href as string}
      className={cn(commonClasses, isActive ? activeClasses : inactiveClasses)}
    >
      {content}
    </Link>
  );
}

function NavigationList({
  isExpanded,
  pathname,
}: {
  isExpanded: boolean;
  pathname: string | null;
}): React.ReactElement {
  return (
    <div
      className={cn(
        'custom-scrollbar-thin w-full flex-1 overflow-y-auto px-3 py-2',
        'overflow-x-hidden'
      )}
    >
      <div className="space-y-1.5">
        {navigation.map((item) => (
          <SidebarNavItemRow
            key={item.href || item.name}
            name={item.name}
            href={item.href || ''}
            Icon={item.icon}
            className={item.className || ''}
            isExpanded={isExpanded}
            pathname={pathname}
          />
        ))}
      </div>
    </div>
  );
}

function UserDropdownMenu({
  session,
  setShowLogoutDialog,
}: {
  session: ReturnType<typeof useSession>;
  setShowLogoutDialog: React.Dispatch<React.SetStateAction<boolean>>;
}): React.ReactElement {
  return (
    <DropdownMenuContent className="w-64 rounded-xl border border-border bg-card p-0 shadow-lg">
      {/* Current User Section */}
      <div className="flex items-center gap-3 px-4 py-3" data-profile-dropdown>
        <Avatar className="h-10 w-10">
          <AvatarImage
            src={
              session.data?.user.avatarUrl ||
              'https://i.pinimg.com/736x/c1/3d/40/c13d4086018c729a871527891cf72b4d.jpg'
            }
          />
          <AvatarFallback>
            <User />
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col items-start">
          <span className="text-base font-semibold text-foreground">
            {session.data?.user.username}
          </span>
          <span className="text-xs text-muted-foreground">
            {censorEmail(session.data?.user.email ?? undefined)}
          </span>
        </div>
      </div>
      <div className="my-1 border-t border-border" />
      <DropdownMenuItem asChild>
        <Link
          href={
            session.data?.user?.username
              ? `/@${encodeURIComponent(session.data.user.username)}`
              : '/settings/profile'
          }
          className="flex w-full items-center gap-2 px-4 py-2"
        >
          <User className="h-4 w-4 text-current" />
          <span>My Profile</span>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link
          href="/settings/profile"
          className="flex w-full items-center gap-2 px-4 py-2"
        >
          <User className="h-4 w-4 text-current" />
          <span>Account</span>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem asChild>
        <Link
          href="/settings"
          className="flex w-full items-center gap-2 px-4 py-2"
        >
          <Settings className="h-4 w-4 text-current" />
          <span>Settings</span>
        </Link>
      </DropdownMenuItem>
      <DropdownMenuItem variant="destructive" asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 px-4 py-2"
          onClick={(e) => {
            e.preventDefault();
            setShowLogoutDialog(true);
          }}
        >
          <LogOut className="h-4 w-4" />
          <span>Sign Out</span>
        </button>
      </DropdownMenuItem>
      <div className="my-1 border-t border-border" />
    </DropdownMenuContent>
  );
}

// Move these out of UserSection to avoid nested component definitions

function SignedOutSection({ isExpanded }: { isExpanded: boolean }) {
  return (
    <div className="mt-auto w-full flex-shrink-0 space-y-2 border-t border-border p-3">
      <div>
        {isExpanded ? (
          <Link
            href="/auth/sign-in"
            className={cn(
              'flex w-full items-center gap-3 px-3 py-2.5',
              'group relative text-sm font-medium',
              'transition-colors duration-200 ease-in-out',
              'rounded-md text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Avatar>
              <AvatarImage src="https://i.pinimg.com/736x/c1/3d/40/c13d4086018c729a871527891cf72b4d.jpg" />
            </Avatar>
            <div
              className={cn(
                'delay-50 whitespace-nowrap transition-opacity duration-200',
                isExpanded ? 'opacity-100' : 'pointer-events-none opacity-0'
              )}
            >
              Sign In
            </div>
          </Link>
        ) : (
          <Avatar>
            <AvatarImage src="https://i.pinimg.com/736x/c1/3d/40/c13d4086018c729a871527891cf72b4d.jpg" />
            <AvatarFallback>
              <User />
            </AvatarFallback>
          </Avatar>
        )}
      </div>
    </div>
  );
}

interface SignedInExpandedSectionProps {
  session: NonNullable<ReturnType<typeof useSession>>;
  setShowLogoutDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDropdownHovered: React.Dispatch<React.SetStateAction<boolean>>;
  user: SessionUser;
}

function SignedInExpandedSection({
  session,
  setShowLogoutDialog,
  setIsDropdownHovered,
  user,
}: SignedInExpandedSectionProps) {
  return (
    <div
      className="cursor-pointer"
      onMouseEnter={() => setIsDropdownHovered(true)}
      onMouseLeave={() => setIsDropdownHovered(false)}
    >
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <div
            className={cn(
              'flex w-full items-center gap-3 px-3 py-2.5',
              'group relative text-sm font-medium',
              'transition-colors duration-200 ease-in-out',
              'rounded-md text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Avatar>
              <AvatarImage
                src={
                  user.image ||
                  'https://i.pinimg.com/736x/c1/3d/40/c13d4086018c729a871527891cf72b4d.jpg'
                }
              />
              <AvatarFallback>
                <User />
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col items-start">
              <span className="text-base font-semibold text-foreground">
                {user.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {censorEmail(user.email ?? undefined)}
              </span>
            </div>
          </div>
        </DropdownMenuTrigger>
        <UserDropdownMenu
          session={session}
          setShowLogoutDialog={setShowLogoutDialog}
        />
      </DropdownMenu>
    </div>
  );
}

interface SignedInCollapsedSectionProps {
  user: SessionUser;
}

function SignedInCollapsedSection({ user }: SignedInCollapsedSectionProps) {
  return (
    <Avatar>
      <AvatarImage
        src={
          user.image ||
          'https://i.pinimg.com/736x/c1/3d/40/c13d4086018c729a871527891cf72b4d.jpg'
        }
      />
      <AvatarFallback>
        <User />
      </AvatarFallback>
    </Avatar>
  );
}

function UserSection({
  isExpanded,
  session,
  setShowLogoutDialog,
  setIsDropdownHovered,
}: {
  isExpanded: boolean;
  session: ReturnType<typeof useSession>;
  setShowLogoutDialog: React.Dispatch<React.SetStateAction<boolean>>;
  setIsDropdownHovered: React.Dispatch<React.SetStateAction<boolean>>;
}): React.ReactElement {
  const user = session.data?.user as SessionUser | undefined;
  if (!user?.id) {
    return <SignedOutSection isExpanded={isExpanded} />;
  }

  // session.data is guaranteed to be non-null here
  return (
    <div className="mt-auto w-full flex-shrink-0 space-y-2 border-t border-border p-3">
      <div>
        {isExpanded ? (
          <SignedInExpandedSection
            session={session as NonNullable<typeof session>}
            setShowLogoutDialog={setShowLogoutDialog}
            setIsDropdownHovered={setIsDropdownHovered}
            user={user}
          />
        ) : (
          <SignedInCollapsedSection user={user} />
        )}
      </div>
    </div>
  );
}

function LogoutDialog({
  onCancel,
  onConfirm,
}: {
  onCancel: () => void;
  onConfirm: () => void;
}): React.ReactElement {
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40">
      <div className="w-full max-w-xs rounded-xl border border-border bg-card p-6 shadow-xl">
        <div className="mb-2 text-lg font-semibold">Sign Out?</div>
        <div className="mb-4 text-muted-foreground">
          Are you sure you want to sign out?
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            className="rounded-md bg-muted px-4 py-2 text-foreground hover:bg-muted/80"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded-md bg-destructive px-4 py-2 text-white hover:bg-destructive/90"
            onClick={onConfirm}
          >
            Log Out
          </button>
        </div>
      </div>
    </div>
  );
}

export function Sidebar(): React.ReactElement {
  const pathname = useLocation().pathname;
  const router = useRouter();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDropdownHovered, setIsDropdownHovered] = useState(false);
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [sidebarLightMode, setSidebarLightMode] = useState(true);
  const session = useSession();

  useEffect(() => {
    const sidebarSetting = localStorage.getItem('sidebarLightMode');
    if (sidebarSetting) {
      setSidebarLightMode(sidebarSetting === 'true');
    } else {
      localStorage.setItem('sidebarLightMode', 'true');
    }
  }, [isExpanded]);

  useEffect(() => {
    if (!sidebarLightMode) {
      const bodyClass = 'sidebar-expanded';
      if (isExpanded) {
        document.body.classList.add(bodyClass);
      } else {
        document.body.classList.remove(bodyClass);
      }
      return () => {
        document.body.classList.remove(bodyClass);
      };
    }
    return () => {};
  }, [isExpanded, sidebarLightMode]);

  useEffect(() => {
    const collapseSidebar = (): void => {
      setIsExpanded(false);
      setIsDropdownHovered(false);
    };

    router.events.on('routeChangeStart', collapseSidebar);
    router.events.on('hashChangeStart', collapseSidebar);

    return () => {
      router.events.off('routeChangeStart', collapseSidebar);
      router.events.off('hashChangeStart', collapseSidebar);
    };
  }, [router.events]);

  // Close dropdowns when clicking outside (profile handled by Radix; no local state)

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch {
      // ignore
    }
  }, []);

  return (
    <>
      <nav
        className={cn(
          'fixed bottom-0 left-0 top-0 z-50',
          'hidden flex-col md:flex',
          'border-r border-border bg-card',
          'overflow-x-hidden',
          'transition-[width] duration-200 ease-out'
        )}
        style={{ width: isExpanded ? 240 : 72 }}
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => {
          if (!isDropdownHovered) setIsExpanded(false);
        }}
      >
        {/* Logo */}
        <SidebarLogo isExpanded={isExpanded} />
        <div className="mb-3 mt-1 w-full flex-shrink-0 px-3">
          <CommandSearch
            isExpanded={isExpanded}
            className={cn(isExpanded ? 'w-full' : 'w-[48px]')}
            showTrigger
          />
        </div>

        <NavigationList isExpanded={isExpanded} pathname={pathname} />

        <UserSection
          isExpanded={isExpanded}
          session={session}
          setShowLogoutDialog={setShowLogoutDialog}
          setIsDropdownHovered={setIsDropdownHovered}
        />
      </nav>
      {showLogoutDialog && (
        <LogoutDialog
          onCancel={() => setShowLogoutDialog(false)}
          onConfirm={() => {
            setShowLogoutDialog(false);
            handleSignOut();
          }}
        />
      )}
    </>
  );
}
